import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || 
          window.navigator.standalone === true) {
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    if (checkInstalled()) {
      return;
    }

    // Check if banner was already shown this session
    const bannerShownThisSession = sessionStorage.getItem('pwa-banner-shown');
    if (bannerShownThisSession === 'true') {
      console.log('[PWA] Banner already shown this session');
      return;
    }

    // Check if user dismissed banner recently (within 7 days)
    const dismissedTime = localStorage.getItem('pwa-banner-dismissed');
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        console.log('[PWA] Banner dismissed recently, not showing');
        return;
      }
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt event fired');
      
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      // Save the event for later use
      setDeferredPrompt(e);
      
      // Show install banner after 3-5 seconds (user requested)
      const delay = 3000 + Math.random() * 2000; // 3-5 seconds
      console.log(`[PWA] Will show banner after ${Math.round(delay/1000)} seconds`);
      
      setTimeout(() => {
        setShowInstallBanner(true);
        // Mark as shown in this session
        sessionStorage.setItem('pwa-banner-shown', 'true');
      }, delay);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App was installed');
      setIsInstalled(true);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No install prompt available');
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`[PWA] User response: ${outcome}`);

    if (outcome === 'accepted') {
      console.log('[PWA] User accepted the install prompt');
    } else {
      console.log('[PWA] User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleCloseBanner = () => {
    console.log('[PWA] User closed install banner');
    setShowInstallBanner(false);
    
    // Save dismissal time to not show again for 7 days
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
    
    // Mark as shown in this session
    sessionStorage.setItem('pwa-banner-shown', 'true');
  };

  // Don't show anything if app is already installed
  if (isInstalled) {
    return null;
  }

  // Install Banner (shows after delay)
  if (showInstallBanner && deferredPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 pb-safe">
        <Card className="max-w-md mx-auto shadow-2xl border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-white rounded-2xl">
          <CardContent className="pt-6 pb-5 px-5 sm:px-6">
            {/* Close button - Large tap target (44x44 minimum for mobile accessibility) */}
            <button
              onClick={handleCloseBanner}
              className="absolute top-1 right-1 p-2.5 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close install prompt"
            >
              <X className="h-6 w-6 text-gray-600 hover:text-gray-800" />
            </button>

            <div className="flex items-start gap-4 pr-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Smartphone className="h-7 w-7 text-white" />
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="font-bold text-lg sm:text-xl mb-1 text-gray-900">
                  📱 Install AMTEE School App
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 leading-relaxed">
                  Get quick access to homework, notices, fees, and more. Works offline and feels like a native app!
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleInstallClick}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-200 py-5 px-6 text-base sm:text-lg font-bold touch-manipulation min-h-[64px]"
                  >
                    <Download className="h-6 w-6 mr-2" />
                    Install Now
                  </Button>
                  <Button
                    onClick={handleCloseBanner}
                    variant="outline"
                    className="border-gray-300 hover:bg-gray-50 py-3 px-4 sm:py-2 min-h-[48px] touch-manipulation"
                  >
                    Maybe Later
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default InstallPWA;
